import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { LogIn } from 'lucide-react';
import { firebaseNet } from './arenaNet';

// A platform fighter: damage builds up as a %, and the higher it is the further hits launch you. Fly past the edge of
// the screen and you lose a stock; the last one with stocks left wins.
//
// Online model: every player simulates their own fighter at 60fps and sends its state to Firebase ~20 times a second.
// When you hit someone, a hit is posted to their inbox (arena/players/{them}/hits) and THEIR game applies the damage and
// knockback, so nobody's position is ever written by two people. The round (lobby → countdown/battle → gameover) is in
// arena/round and run by the "host": whoever has been in the lobby longest, so it carries on if someone leaves.
// The connection is swappable (arenaNet.js): the website uses Firebase's live connection, the Discord Activity its web API.

const SPRITES = 'https://raw.githubusercontent.com';
const spriteFrom = base => n => `${base}/PokeAPI/sprites/master/sprites/pokemon/${n}.png`;
const STALE_MS = 12000;   // someone who checks in (the Discord version) and hasn't for this long has gone

// run/air: top speed on the ground / in the air. jump: jump strength. weight: heavier = launched less far.
// jumps: total jumps including the one from the ground. proj: [speed, damage, size] of the special.
const ROSTER = [
  { id: 'pikachu',    name: 'Pikachu',    dex: 25,  color: '#FFD700', special: 'Thunderbolt',  run: 5.2, air: 3.6, jump: 10.8, weight: 80,  jumps: 2, proj: [7.5, 6, 9] },
  { id: 'charizard',  name: 'Charizard',  dex: 6,   color: '#FF6B35', special: 'Flamethrower', run: 4.6, air: 3.4, jump: 10.2, weight: 115, jumps: 3, proj: [6, 9, 12] },
  { id: 'gengar',     name: 'Gengar',     dex: 94,  color: '#8B5CF6', special: 'Shadow Ball',  run: 4.4, air: 3.4, jump: 10.4, weight: 95,  jumps: 2, proj: [5, 9, 13] },
  { id: 'mewtwo',     name: 'Mewtwo',     dex: 150, color: '#A78BFA', special: 'Psystrike',    run: 4.8, air: 3.6, jump: 10.6, weight: 90,  jumps: 2, proj: [7.5, 8, 12] },
  { id: 'snorlax',    name: 'Snorlax',    dex: 143, color: '#9CA3AF', special: 'Body Slam',    run: 3.4, air: 2.6, jump: 9.4,  weight: 140, jumps: 2, proj: [4.5, 11, 15] },
  { id: 'togepi',     name: 'Togepi',     dex: 175, color: '#FCD34D', special: 'Metronome',    run: 4.0, air: 3.2, jump: 10.0, weight: 75,  jumps: 3, proj: [6, 0, 10], random: true },
  { id: 'lucario',    name: 'Lucario',    dex: 448, color: '#3B82F6', special: 'Aura Sphere',  run: 4.9, air: 3.5, jump: 10.6, weight: 95,  jumps: 2, proj: [7, 8, 11] },
  { id: 'gardevoir',  name: 'Gardevoir',  dex: 282, color: '#EC4899', special: 'Moonblast',    run: 4.2, air: 3.3, jump: 10.2, weight: 85,  jumps: 2, proj: [6.5, 8, 11] },
  { id: 'blaziken',   name: 'Blaziken',   dex: 257, color: '#F97316', special: 'Blaze Kick',   run: 5.0, air: 3.4, jump: 11.2, weight: 100, jumps: 2, proj: [8, 7, 9] },
  { id: 'umbreon',    name: 'Umbreon',    dex: 197, color: '#60A5FA', special: 'Dark Pulse',   run: 4.6, air: 3.3, jump: 10.4, weight: 105, jumps: 2, proj: [6.5, 8, 11] },
  { id: 'dragonite',  name: 'Dragonite',  dex: 149, color: '#F59E0B', special: 'Hyper Beam',   run: 4.3, air: 3.3, jump: 10.0, weight: 125, jumps: 3, proj: [6, 10, 13] },
  { id: 'eevee',      name: 'Eevee',      dex: 133, color: '#D97706', special: 'Swift',        run: 5.3, air: 3.6, jump: 10.8, weight: 75,  jumps: 2, proj: [8, 5, 8] },
  { id: 'mew',        name: 'Mew',        dex: 151, color: '#F9A8D4', special: 'Transform',    run: 4.6, air: 3.6, jump: 10.2, weight: 70,  jumps: 4, proj: [6.5, 7, 10] },
  { id: 'gyarados',   name: 'Gyarados',   dex: 130, color: '#1D4ED8', special: 'Hydro Pump',   run: 3.8, air: 3.0, jump: 9.6,  weight: 135, jumps: 2, proj: [7, 10, 13] },
  { id: 'jigglypuff', name: 'Jigglypuff', dex: 39,  color: '#FCA5A5', special: 'Sing',         run: 3.8, air: 3.8, jump: 9.4,  weight: 65,  jumps: 5, proj: [5, 6, 10] },
];
const POKE = Object.fromEntries(ROSTER.map(p => [p.id, p]));
const DUMMY = { id: 'dummy', name: 'Dummy', color: '#d6c39a', run: 0, air: 0, jump: 0, weight: 100, jumps: 1, proj: [0, 0, 0] };

// ─── World ─────────────────────────────────────────────────────────────────────
const W = 800, H = 450;                                   // the canvas, in game units
const STAGE = { x1: 150, x2: 650, top: 330, bottom: 372 }; // the solid main stage
const PLATS = [                                            // drop-through platforms
  { x1: 205, x2: 345, y: 245 },
  { x1: 455, x2: 595, y: 245 },
  { x1: 330, x2: 470, y: 165 },
];
const BLAST = { l: -170, r: W + 170, t: -210, b: H + 170 }; // leave this box and you lose a stock
const HB_W = 36, HB_H = 44;                                // hurtbox (x is the middle, y is the feet)
const GRAV = 0.5, MAX_FALL = 8.5, FAST_FALL = 12;
const STOCKS = 3;
const MAX_FIGHTERS = 8;
const SHIELD_MAX = 60;
const STEP_MS = 1000 / 60;
const SEND_EVERY = 3;                                      // send my state every 3 frames (~20 a second)

// Attacks. s/a/e: startup, active and end frames. box: [forward offset, offset up from the feet, width, height].
// ang: launch angle in degrees (90 = straight up, negative = down). base/growth: knockback at 0% and how fast it grows.
const ATTACKS = {
  jab:  { s: 3, a: 3,  e: 7,  dmg: 3,  base: 3.5, growth: 0.03, ang: 35,  box: [22, -24, 30, 22] },
  side: { s: 9, a: 4,  e: 16, dmg: 12, base: 4.5, growth: 0.12, ang: 38,  box: [28, -26, 40, 26] },
  up:   { s: 6, a: 5,  e: 13, dmg: 9,  base: 4.5, growth: 0.085, ang: 86, box: [0, -58, 50, 30] },
  down: { s: 5, a: 4,  e: 12, dmg: 7,  base: 4,   growth: 0.07, ang: 22,  box: [0, -8, 86, 18] },
  nair: { s: 4, a: 6,  e: 10, dmg: 7,  base: 3.5, growth: 0.07, ang: 45,  box: [0, -22, 58, 52] },
  fair: { s: 6, a: 4,  e: 12, dmg: 10, base: 4,   growth: 0.10, ang: 40,  box: [26, -24, 38, 30] },
  uair: { s: 4, a: 4,  e: 10, dmg: 8,  base: 4,   growth: 0.075, ang: 80, box: [0, -56, 48, 26] },
  dair: { s: 8, a: 4,  e: 16, dmg: 11, base: 3.5, growth: 0.09, ang: -80, box: [0, 6, 36, 28] },
  upb:  { s: 1, a: 16, e: 2,  dmg: 5,  base: 3.5, growth: 0.05, ang: 80,  box: [0, -30, 50, 52] },
  shoot:{ s: 0, a: 0,  e: 12 },
};
const AIR_ATTACKS = new Set(['nair', 'fair', 'uair', 'dair']);
const PROJ_HIT = { base: 3, growth: 0.045, ang: 30 };

// ─── Physics ───────────────────────────────────────────────────────────────────
const newBody = (x, f) => ({
  x, y: STAGE.top, vx: 0, vy: 0, f, ground: true, jumps: 1, upb: false, helpless: false, ff: false, drop: 0,
  act: null, actF: 0, actId: 0, hitSet: new Set(), hs: 0, tumble: false, freeze: 0, land: 0,
  pct: 0, st: STOCKS, out: false, inv: 0, sh: SHIELD_MAX, shielding: false, stun: 0, rsp: 0, halo: 0,
  cd: 0, proj: [], flash: 0, spin: 0,
});

const overlap = (a, b) => a[0] < b[0] + b[2] && a[0] + a[2] > b[0] && a[1] < b[1] + b[3] && a[1] + a[3] > b[1];
const hurtbox = (x, y) => [x - HB_W / 2, y - HB_H, HB_W, HB_H];
const attackBox = (b, def) => {
  const [ox, oy, w, h] = def.box;
  return [b.x + ox * b.f - w / 2, b.y + oy - h / 2, w, h];
};
const onSoftPlatform = b => PLATS.some(p => Math.abs(b.y - p.y) < 0.5 && b.x + HB_W / 2 > p.x1 && b.x - HB_W / 2 < p.x2);

function collide(b, prevY) {
  const wasGround = b.ground;
  b.ground = false;
  // Drop-through platforms: only land on them from above, and not while dropping through
  if (!b.drop && b.vy >= 0) {
    for (const p of PLATS) {
      if (prevY <= p.y + 0.01 && b.y >= p.y && b.x + HB_W / 2 > p.x1 && b.x - HB_W / 2 < p.x2) {
        b.y = p.y; b.vy = 0; b.ground = true;
      }
    }
  }
  // The main stage is solid: land on it, bonk your head under it, or get pushed off its sides
  const [l, t, w, h] = hurtbox(b.x, b.y);
  if (l < STAGE.x2 && l + w > STAGE.x1 && t < STAGE.bottom && t + h > STAGE.top) {
    if (prevY <= STAGE.top + 0.01) { b.y = STAGE.top; b.vy = 0; b.ground = true; }
    else if (prevY - HB_H >= STAGE.bottom - 0.01) { b.y = STAGE.bottom + HB_H; b.vy = Math.max(0, b.vy); }
    else { b.x = b.x < (STAGE.x1 + STAGE.x2) / 2 ? STAGE.x1 - HB_W / 2 : STAGE.x2 + HB_W / 2; b.vx = 0; }
  }
  return b.ground && !wasGround;
}

// One frame of a fighter. inp: { left, right, up, down, shield, jumpP, atkP, spP, downP } (P = just pressed).
// Returns 'ko' when they left the screen.
function stepBody(b, ch, inp) {
  if (b.out) return null;
  if (b.rsp > 0) {
    if (--b.rsp === 0) {
      Object.assign(b, { x: W / 2, y: 110, vx: 0, vy: 0, pct: 0, inv: 120, halo: 90, act: null, hs: 0, stun: 0,
                         helpless: false, upb: false, jumps: ch.jumps - 1, ff: false, ground: false, shielding: false });
    }
    return null;
  }
  if (b.freeze > 0) { b.freeze--; return null; }   // hit-pause: a short freeze on a hit sells the impact
  const prevY = b.y;
  if (b.inv > 0) b.inv--;
  if (b.cd > 0) b.cd--;
  if (b.land > 0) b.land--;
  if (b.flash > 0) b.flash--;
  if (b.drop > 0) b.drop--;
  const dirX = (inp.right ? 1 : 0) - (inp.left ? 1 : 0);

  if (b.halo > 0) {   // respawned: float on the halo until you move or it runs out
    b.halo--;
    if (dirX || inp.jumpP || inp.atkP || inp.spP || inp.downP) b.halo = 0;
    else { b.vx = 0; b.vy = 0; return null; }
  }

  if (b.hs > 0 || b.stun > 0) {
    // Launched (hitstun) or shield broken (stunned): no control
    if (b.hs > 0) b.hs--; else b.stun--;
    b.spin += b.tumble && b.hs > 0 ? 0.35 * Math.sign(b.vx || 1) : 0;
    if (b.hs === 0) b.tumble = false;
    b.vx *= b.ground ? 0.85 : 0.95;
    b.shielding = false;
  } else {
    b.spin = 0;
    // An attack in progress
    if (b.act) {
      const def = ATTACKS[b.act];
      if (++b.actF >= def.s + def.a + def.e) {
        if (b.act === 'upb' && !b.ground) b.helpless = true;
        b.act = null;
      }
    }
    const canAct = !b.act && b.land === 0;
    // Shield: hold it on the ground. It shrinks while held, and breaks if hit too much
    b.shielding = canAct && b.ground && inp.shield && b.sh > 0;
    if (b.shielding) {
      b.sh -= 0.2;
      b.vx *= 0.7;
      if (b.sh <= 0) { b.sh = 0; b.shielding = false; b.stun = 140; b.vy = -9; b.ground = false; }
    } else if (b.sh < SHIELD_MAX) {
      b.sh = Math.min(SHIELD_MAX, b.sh + 0.12);
    }

    // Running and drifting
    if (b.ground) {
      if (dirX && !b.shielding && (!b.act)) { b.vx = Math.max(-ch.run, Math.min(ch.run, b.vx + dirX * ch.run * 0.35)); b.f = dirX; }
      else b.vx *= b.act ? 0.8 : 0.72;
    } else {
      if (dirX) b.vx = Math.max(-ch.air, Math.min(ch.air, b.vx + dirX * 0.32));
      else b.vx *= 0.985;
    }

    // Jumping (also out of shield)
    if (inp.jumpP && !b.helpless && (canAct || b.shielding)) {
      if (b.ground) { b.vy = -ch.jump; b.ground = false; b.jumps = ch.jumps - 1; b.ff = false; b.shielding = false; }
      else if (b.jumps > 0) { b.vy = -ch.jump * 0.92; b.jumps--; b.ff = false; if (dirX) b.vx = dirX * ch.air; }
    }

    // Down: drop through a platform, or fast-fall in the air
    if (inp.downP && canAct && !b.shielding) {
      if (b.ground && onSoftPlatform(b)) { b.drop = 10; b.ground = false; b.y += 1; }
      else if (!b.ground && b.vy > -2) b.ff = true;
    }

    // Attacks: the direction held picks the move
    if (inp.atkP && canAct && !b.shielding && !b.helpless) {
      let name;
      if (b.ground) {
        name = inp.up ? 'up' : inp.down ? 'down' : dirX ? 'side' : 'jab';
        if (dirX) b.f = dirX;
      } else {
        name = inp.up ? 'uair' : inp.down ? 'dair' : dirX ? 'fair' : 'nair';
        if (dirX) b.f = dirX;
      }
      startAttack(b, name);
    }
    // Special: up = recovery leap, anything else = the projectile
    if (inp.spP && canAct && !b.shielding && !b.helpless) {
      if (inp.up) {
        if (!b.upb) {
          startAttack(b, 'upb');
          b.upb = true; b.ground = false; b.ff = false;
          b.vy = -ch.jump * 1.35;
          b.vx = (dirX || b.f) * 2.5;
          if (dirX) b.f = dirX;
        }
      } else if (b.cd === 0 && b.proj.length === 0 && ch.proj[0]) {
        if (dirX) b.f = dirX;
        const dmg = ch.random ? 3 + Math.floor(Math.random() * 12) : ch.proj[1];
        b.proj.push({ x: b.x + b.f * 22, y: b.y - 26, vx: b.f * ch.proj[0], r: ch.proj[2], dmg, life: 80, hit: new Set() });
        b.cd = 36;
        startAttack(b, 'shoot');
      }
    }
  }

  // Gravity
  if (b.ff && b.hs === 0 && b.vy > 0) b.vy = FAST_FALL;
  else b.vy = Math.min(b.vy + GRAV, b.hs > 0 ? 15 : MAX_FALL);
  b.x += b.vx;
  b.y += b.vy;
  const fallSpeed = b.vy;
  if (collide(b, prevY)) {
    // Landed
    b.jumps = ch.jumps - 1; b.upb = false; b.helpless = false; b.ff = false;
    if (b.act && (AIR_ATTACKS.has(b.act) || b.act === 'upb')) { b.act = null; b.land = 6; }
    if (b.hs > 0 && fallSpeed > 7) { b.vy = -fallSpeed * 0.45; b.ground = false; }  // slammed into the floor: bounce
  }

  // Projectiles
  b.proj = b.proj.filter(p => {
    p.x += p.vx; p.life--;
    const hitStage = p.x > STAGE.x1 && p.x < STAGE.x2 && p.y > STAGE.top && p.y < STAGE.bottom;
    return p.life > 0 && !hitStage && p.x > -40 && p.x < W + 40;
  });

  // Off the screen: lose a stock
  if (b.x < BLAST.l || b.x > BLAST.r || b.y < BLAST.t || b.y > BLAST.b) {
    b.st = Number.isFinite(b.st) ? b.st - 1 : b.st;
    b.out = b.st <= 0;
    Object.assign(b, { rsp: b.out ? 0 : 60, act: null, hs: 0, stun: 0, proj: [], shielding: false, tumble: false });
    return 'ko';
  }
  return null;
}

function startAttack(b, name) {
  b.act = name; b.actF = 0; b.actId++; b.hitSet = new Set();
}

// A hit lands on body b (my own fighter, or the training dummy). Returns the knockback, 'shield', or null.
function applyHit(b, ch, hit) {
  if (b.out || b.rsp > 0 || b.inv > 0) return null;
  if (b.shielding) {
    b.sh -= hit.dmg * 1.4;
    b.vx = hit.dir * 2.2;
    if (b.sh <= 0) { b.sh = 0; b.shielding = false; b.stun = 140; b.vy = -9; b.ground = false; }
    return 'shield';
  }
  b.pct = Math.min(999, b.pct + hit.dmg);
  const kb = (hit.base + b.pct * hit.growth * (1 + hit.dmg / 20)) * (200 / (100 + ch.weight));
  const a = hit.ang * Math.PI / 180;
  b.vx = Math.cos(a) * kb * hit.dir;
  b.vy = -Math.sin(a) * kb;
  if (b.ground && b.vy > 0) b.vy = -b.vy * 0.6;   // spiked while standing: popped up instead
  Object.assign(b, { hs: Math.round(8 + kb * 1.6), tumble: kb > 12, ground: false, act: null, ff: false,
                     helpless: false, upb: false, halo: 0, shielding: false, flash: 8,
                     freeze: Math.min(8, 2 + Math.round(hit.dmg / 3)) });
  b.y -= 1;
  return kb;
}

// ─── Drawing ───────────────────────────────────────────────────────────────────
const STARS = Array.from({ length: 60 }, (_, i) => ({
  x: (i * 137.5) % W, y: ((i * 71.3) % (H * 0.7)), r: (i % 3) * 0.5 + 0.6, a: 0.15 + (i % 5) * 0.1,
}));
const pctColor = pct => {
  const t = Math.min(1, pct / 150);
  return `rgb(255,${Math.round(255 - t * 200)},${Math.round(255 - t * 230)})`;
};

function drawStage(ctx) {
  const sky = ctx.createLinearGradient(0, 0, 0, H);
  sky.addColorStop(0, '#0c1445'); sky.addColorStop(0.55, '#1a1a2e'); sky.addColorStop(1, '#120a1f');
  ctx.fillStyle = sky; ctx.fillRect(0, 0, W, H);
  for (const s of STARS) { ctx.globalAlpha = s.a; ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, 7); ctx.fill(); }
  ctx.globalAlpha = 1;
  // Main stage
  const g = ctx.createLinearGradient(0, STAGE.top, 0, STAGE.bottom + 30);
  g.addColorStop(0, '#2a3358'); g.addColorStop(1, '#0d1024');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.moveTo(STAGE.x1, STAGE.top); ctx.lineTo(STAGE.x2, STAGE.top);
  ctx.lineTo(STAGE.x2 - 20, STAGE.bottom); ctx.lineTo(STAGE.x2 - 80, STAGE.bottom + 34);
  ctx.lineTo(STAGE.x1 + 80, STAGE.bottom + 34); ctx.lineTo(STAGE.x1 + 20, STAGE.bottom); ctx.closePath(); ctx.fill();
  ctx.shadowColor = '#04aff7'; ctx.shadowBlur = 12;
  ctx.fillStyle = '#04aff7'; ctx.fillRect(STAGE.x1, STAGE.top - 2, STAGE.x2 - STAGE.x1, 4);
  // Platforms
  for (const p of PLATS) {
    ctx.shadowColor = '#f42ae4'; ctx.fillStyle = 'rgba(244,42,228,0.75)';
    ctx.fillRect(p.x1, p.y - 2, p.x2 - p.x1, 5);
  }
  ctx.shadowBlur = 0;
}

function drawFighter(ctx, imgs, s, frame) {
  // s: { x, y, f, poke, act, hs, tumble, spin, inv, sh, flash, name, isMe, halo }
  const ch = s.poke === 'dummy' ? DUMMY : POKE[s.poke];
  if (!ch) return;
  if (s.inv && frame % 8 < 3) ctx.globalAlpha = 0.45;
  if (s.halo) {   // respawn halo
    ctx.fillStyle = 'rgba(255,255,255,0.8)'; ctx.fillRect(s.x - 26, s.y, 52, 4);
  }
  ctx.save();
  ctx.translate(s.x, s.y - HB_H / 2);
  if (s.spin) ctx.rotate(s.spin);
  if (s.poke === 'dummy') {
    ctx.fillStyle = s.flash ? '#fff' : ch.color;
    ctx.beginPath(); ctx.ellipse(0, 2, 18, 22, 0, 0, 7); ctx.fill();
    ctx.fillStyle = '#8a7650'; ctx.fillRect(-6, -24, 12, 6);
    ctx.fillStyle = '#3b2f1c'; ctx.fillRect(-7, -2, 3, 3); ctx.fillRect(4, -2, 3, 3);
  } else {
    const img = imgs[ch.id];
    if (img && img.complete && img.naturalWidth) {
      if (s.f === 1) ctx.scale(-1, 1);   // the sprites face left
      if (s.flash) ctx.filter = 'brightness(3) saturate(0)';
      ctx.drawImage(img, -38, -50, 76, 76);
      ctx.filter = 'none';
    } else {
      ctx.fillStyle = ch.color; ctx.beginPath(); ctx.arc(0, 0, 18, 0, 7); ctx.fill();
    }
  }
  ctx.restore();
  // Attack swoosh
  const def = s.act && ATTACKS[s.act];
  if (def && def.box) {
    const [x, y, w, h] = attackBox({ x: s.x, y: s.y, f: s.f }, def);
    const active = s.actF === undefined || (s.actF >= def.s && s.actF < def.s + def.a);
    if (active) {
      ctx.fillStyle = ch.color; ctx.globalAlpha *= 0.45;
      ctx.beginPath(); ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, 7); ctx.fill();
      ctx.globalAlpha = s.inv && frame % 8 < 3 ? 0.45 : 1;
    }
  }
  // Shield bubble
  if (s.sh) {
    const r = 14 + 22 * (s.sh / SHIELD_MAX);
    ctx.fillStyle = `${ch.color}55`; ctx.strokeStyle = ch.color; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(s.x, s.y - HB_H / 2, r, 0, 7); ctx.fill(); ctx.stroke();
  }
  ctx.globalAlpha = 1;
  // Name tag
  ctx.font = 'bold 10px system-ui, sans-serif';
  const label = s.name.length > 12 ? s.name.slice(0, 11) + '…' : s.name;
  const tw = ctx.measureText(label).width + 10;
  ctx.fillStyle = s.isMe ? 'rgba(4,175,247,0.9)' : 'rgba(0,0,0,0.65)';
  roundRect(ctx, s.x - tw / 2, s.y - HB_H - 30, tw, 15, 5); ctx.fill();
  ctx.fillStyle = '#fff'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText(label, s.x, s.y - HB_H - 22.5);
}

function drawProjectile(ctx, p, color, frame) {
  const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 1.8);
  g.addColorStop(0, '#fff'); g.addColorStop(0.35, color); g.addColorStop(1, 'transparent');
  ctx.fillStyle = g;
  ctx.beginPath(); ctx.arc(p.x, p.y, p.r * (1.7 + 0.15 * Math.sin(frame / 3)), 0, 7); ctx.fill();
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
}

// Fighters who are off the screen get a bubble at the edge so you can see where they are
function drawOffscreen(ctx, imgs, s) {
  if (s.x >= 0 && s.x <= W && s.y >= 0) return;
  const cx = Math.max(22, Math.min(W - 22, s.x)), cy = Math.max(22, Math.min(H - 60, s.y - HB_H / 2));
  const ch = s.poke === 'dummy' ? DUMMY : POKE[s.poke];
  ctx.fillStyle = 'rgba(0,0,0,0.7)'; ctx.strokeStyle = ch?.color || '#fff'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.arc(cx, cy, 18, 0, 7); ctx.fill(); ctx.stroke();
  const img = ch && imgs[ch.id];
  if (img && img.complete && img.naturalWidth) ctx.drawImage(img, cx - 20, cy - 22, 40, 40);
}

function drawHud(ctx, imgs, cards) {
  if (!cards.length) return;
  const cw = Math.min(140, (W - 20) / cards.length), y = H - 46;
  cards.forEach((c, i) => {
    const x = (W - cw * cards.length) / 2 + i * cw;
    const ch = c.poke === 'dummy' ? DUMMY : POKE[c.poke];
    ctx.globalAlpha = c.out ? 0.35 : 1;
    ctx.fillStyle = c.isMe ? 'rgba(4,175,247,0.22)' : 'rgba(0,0,0,0.55)';
    roundRect(ctx, x + 3, y, cw - 6, 42, 8); ctx.fill();
    const img = ch && imgs[ch.id];
    if (img && img.complete && img.naturalWidth) ctx.drawImage(img, x + 2, y - 6, 44, 44);
    ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
    ctx.font = 'bold 9px system-ui, sans-serif'; ctx.fillStyle = '#d4d4d8';
    const name = c.name.length > 10 ? c.name.slice(0, 9) + '…' : c.name;
    ctx.fillText(name, x + 44, y + 12);
    ctx.font = '900 19px system-ui, sans-serif'; ctx.fillStyle = c.out ? '#71717a' : pctColor(c.pct);
    ctx.fillText(c.out ? 'OUT' : `${Math.round(c.pct)}%`, x + 44, y + 32);
    if (Number.isFinite(c.st)) {
      for (let k = 0; k < STOCKS; k++) {
        ctx.fillStyle = k < c.st ? (ch?.color || '#04aff7') : 'rgba(255,255,255,0.15)';
        ctx.beginPath(); ctx.arc(x + 48 + k * 9, y + 38.5, 3, 0, 7); ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
  });
}

function bigText(ctx, text, size, color = '#fff') {
  ctx.font = `900 ${size}px system-ui, sans-serif`;
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.lineWidth = 8; ctx.strokeStyle = 'rgba(0,0,0,0.6)'; ctx.strokeText(text, W / 2, H / 2 - 20);
  ctx.shadowColor = '#04aff7'; ctx.shadowBlur = 24; ctx.fillStyle = color; ctx.fillText(text, W / 2, H / 2 - 20);
  ctx.shadowBlur = 0;
}

// ─── Component ─────────────────────────────────────────────────────────────────
const EMPTY_INPUT = () => ({ left: false, right: false, up: false, down: false, shield: false,
                             jumpP: false, atkP: false, spP: false, downP: false });
const KEYMAP = {
  ArrowLeft: 'left', a: 'left', A: 'left', ArrowRight: 'right', d: 'right', D: 'right',
  ArrowUp: 'up', w: 'up', W: 'up', ArrowDown: 'down', s: 'down', S: 'down',
  ' ': 'jump', j: 'attack', J: 'attack', z: 'attack', Z: 'attack',
  k: 'special', K: 'special', x: 'special', X: 'special', l: 'shield', L: 'shield', c: 'shield', C: 'shield', Shift: 'shield',
};

export default function BattleArena({ database, net: netProp, discordUser, isSpectator = false, onLogin, spriteBase = SPRITES, activity = false }) {
  const myUid = discordUser?.id;
  const sprite = useMemo(() => spriteFrom(spriteBase), [spriteBase]);
  const [players, setPlayers] = useState({});  // who's in the lobby (no positions: those stay out of React)
  const [round, setRound] = useState(null);

  const netRef = useRef({});          // everyone's latest data from Firebase, positions included
  const smoothRef = useRef({});       // uid -> where they're drawn (eased towards their latest position)
  const roundRef = useRef(null);
  const offsetRef = useRef(0);        // server clock minus this one, so countdowns line up
  const bodyRef = useRef(null);       // my fighter
  const dummyRef = useRef(null);      // the training dummy, when you're the only fighter
  const simRoundRef = useRef(null);   // the round my fighter was set up for
  const inputRef = useRef(EMPTY_INPUT());
  const pendingHitsRef = useRef([]);
  const fxRef = useRef({ pops: [], sparks: [], blasts: [], shake: 0 });
  const canvasRef = useRef(null);
  const imgsRef = useRef({});
  const frameRef = useRef(0);

  const net = useMemo(() => netProp || firebaseNet(database), [netProp, database]);
  const mePath = myUid ? `players/${myUid}` : null;
  const serverNow = () => Date.now() + offsetRef.current;

  // Sprites, loaded once for drawing on the canvas
  useEffect(() => {
    for (const p of ROSTER) {
      if (imgsRef.current[p.id]) continue;
      const img = new Image();
      img.src = sprite(p.dex);
      imgsRef.current[p.id] = img;
    }
  }, [sprite]);

  // Server clock offset
  useEffect(() => net.onClockOffset(o => { offsetRef.current = o; }), [net]);

  // Everyone's data. Positions go into a ref (they change 20 times a second); React only re-renders when the
  // lobby itself changes (someone joins, leaves, readies up or changes fighter)
  useEffect(() => net.onPlayers(data => {
    const prev = netRef.current;
    const nowMs = performance.now();
    for (const [uid, p] of Object.entries(data)) {
      const old = prev[uid];
      if (old && p.rid !== undefined && old.rid === p.rid) {
        // Someone took damage or lost a stock: sparks / a KO blast where they are (whoever hit them)
        const pos = smoothRef.current[uid];
        if (pos && uid !== myUid && (p.pct || 0) > (old.pct || 0)) addSpark(pos.x, pos.y - HB_H / 2, POKE[p.poke]?.color);
        if (pos && uid !== myUid && (p.st ?? STOCKS) < (old.st ?? STOCKS)) addBlast(pos.x, pos.y, POKE[p.poke]?.color);
      }
      p._recv = old && old.x === p.x && old.y === p.y ? old._recv : nowMs;
    }
    netRef.current = data;
    setPlayers(current => {
      const meta = {};
      for (const [uid, p] of Object.entries(data)) {
        if (!p.poke) continue;   // not a real player (see the host's tidy-up)
        meta[uid] = { uid, name: p.name || 'Player', avatar: p.avatar || null, poke: p.poke, ready: !!p.ready, joinedAt: p.joinedAt || 0 };
      }
      return JSON.stringify(meta) === JSON.stringify(current) ? current : meta;
    });
  }), [net, myUid]); // eslint-disable-line react-hooks/exhaustive-deps

  // The round
  useEffect(() => net.onRound(r => {
    roundRef.current = r;
    setRound(r);
  }), [net]);

  const joined = !!(myUid && players[myUid]);

  // Hits other players land on me arrive here; my own game applies them
  useEffect(() => {
    if (!joined) return;
    return net.onHits(myUid, hit => {
      if (hit && serverNow() - (hit.at || 0) < 2000) pendingHitsRef.current.push(hit);
    });
  }, [joined, net, myUid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Leaving the page or the tab takes you out of the arena
  useEffect(() => () => { if (myUid) Promise.resolve(net.leave(myUid)).catch(() => {}); }, [net, myUid]);

  // ─── Host duties: start the countdown, call the winner, go back to the lobby ─────
  useEffect(() => {
    if (!myUid) return;
    // Back to the lobby, with everyone's Ready cleared in the same write (so a stale "ready" can't restart it at once)
    const toLobby = (r, list) => {
      const changes = { round: { id: r.id, phase: 'lobby' } };
      for (const p of list) changes[`players/${p.uid}/ready`] = false;
      net.update('', changes).catch(() => {});
    };
    const id = setInterval(() => {
      const ps = netRef.current;
      const now = serverNow();
      const gone = p => !p.poke || (typeof p.seen === 'number' && now - p.seen > STALE_MS);
      const list = Object.entries(ps).map(([uid, p]) => ({ ...p, uid })).filter(p => !gone(p))
        .sort((a, b) => (a.joinedAt || Infinity) - (b.joinedAt || Infinity) || a.uid.localeCompare(b.uid));
      if (!list.length || list[0].uid !== myUid) return;
      // A hit sent to someone just as they left leaves a stray entry with no fighter, and someone in Discord who
      // stopped checking in has closed the game: remove those
      for (const [uid, p] of Object.entries(ps)) if (gone(p)) net.remove(`players/${uid}`).catch(() => {});
      const r = roundRef.current;
      const phase = r?.phase || 'lobby';
      if (phase === 'lobby') {
        if (list.every(p => p.ready)) {
          const fighters = Object.fromEntries(list.slice(0, MAX_FIGHTERS).map(p => [p.uid, true]));
          net.set('round', { id: now, phase: 'countdown', startAt: now + 3500, fighters }).catch(() => {});
        }
      } else if (phase === 'countdown') {
        if (now < r.startAt + 1000) return;
        const fighters = Object.keys(r.fighters || {});
        const present = fighters.filter(u => ps[u] && !gone(ps[u]));
        const alive = present.filter(u => ps[u].rid !== r.id || !ps[u].out);
        // Practice on your own ends when you leave, or when someone else turns up to play
        if (!present.length || (fighters.length === 1 && (!alive.length || list.some(p => !r.fighters[p.uid])))) {
          toLobby(r, list);
        } else if (fighters.length >= 2 && alive.length <= 1) {
          net.set('round', { ...r, phase: 'gameover', winner: alive[0] || null, winnerName: alive[0] ? (ps[alive[0]].name || 'Player') : null, endAt: now }).catch(() => {});
        }
      } else if (phase === 'gameover' && now > (r.endAt || 0) + 5000) {
        toLobby(r, list);
      }
    }, 400);
    // Tidy up the old version's data
    if (!activity) net.update('', { phase: null, countdown: null, winner: null }).catch(() => {});
    return () => clearInterval(id);
  }, [myUid, net]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Joining and leaving ───────────────────────────────────────────────────
  const joinLobby = useCallback(async (pokeId) => {
    if (!myUid || !discordUser) return;
    await net.join(myUid, {
      uid: myUid, name: String((activity && discordUser.nickname) || discordUser.global_name || discordUser.username || 'Player').slice(0, 64),
      avatar: discordUser.avatar ? `https://cdn.discordapp.com/avatars/${myUid}/${discordUser.avatar}.png` : null,
      poke: pokeId, ready: false,
    });
  }, [net, myUid, discordUser]);

  const toggleReady = useCallback(() => {
    if (mePath && players[myUid]) net.update(mePath, { ready: !players[myUid].ready }).catch(() => {});
  }, [net, mePath, players, myUid]);

  const leaveArena = useCallback(() => {
    if (myUid) Promise.resolve(net.leave(myUid)).catch(() => {});
    bodyRef.current = null;
    simRoundRef.current = null;
  }, [net, myUid]);

  // ─── Effects ───────────────────────────────────────────────────────────────
  function addSpark(x, y, color = '#fff', big = false) { fxRef.current.sparks.push({ x, y, color, t: 0, life: big ? 18 : 12, big }); }
  function addPop(x, y, text, color) { fxRef.current.pops.push({ x, y, text, color, t: 0, life: 45 }); }
  function addBlast(x, y, color = '#fff') {
    const cx = Math.max(10, Math.min(W - 10, x)), cy = Math.max(10, Math.min(H - 10, y));
    fxRef.current.blasts.push({ x: cx, y: cy, ang: Math.atan2(H / 2 - cy, W / 2 - cx), color, t: 0, life: 40 });
    fxRef.current.shake = 14;
  }

  // ─── Phase / view ──────────────────────────────────────────────────────────
  const phase = round?.phase || 'lobby';
  const arenaView = joined && (phase === 'countdown' || phase === 'gameover');

  // ─── Keyboard ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!arenaView) return;
    const press = (action, down) => {
      const inp = inputRef.current;
      if (action === 'jump') { if (down) inp.jumpP = true; }
      else if (action === 'attack') { if (down) inp.atkP = true; }
      else if (action === 'special') { if (down) inp.spP = true; }
      else {
        if (action === 'down' && down && !inp.down) inp.downP = true;
        inp[action] = down;
      }
    };
    const onKey = down => e => {
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      const action = KEYMAP[e.key];
      if (!action) return;
      e.preventDefault();   // no page scrolling from the arrows and space
      if (down && e.repeat && ['jump', 'attack', 'special', 'down'].includes(action)) return;
      press(action, down);
    };
    const kd = onKey(true), ku = onKey(false);
    const blur = () => { inputRef.current = EMPTY_INPUT(); };
    window.addEventListener('keydown', kd);
    window.addEventListener('keyup', ku);
    window.addEventListener('blur', blur);
    return () => { window.removeEventListener('keydown', kd); window.removeEventListener('keyup', ku); window.removeEventListener('blur', blur); };
  }, [arenaView]);

  // ─── The game loop ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!arenaView) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = W * dpr; canvas.height = H * dpr;

    const sendHit = (uid, hit) => {
      net.push(`players/${uid}/hits`, { ...hit, from: myUid, at: serverNow() }).catch(() => {});
    };

    // Everyone I can hit this frame: other fighters (where I see them) and the dummy
    const targets = (r) => {
      const list = [];
      for (const [uid, p] of Object.entries(netRef.current)) {
        if (uid === myUid || p.rid !== r.id || p.out || p.rsp || p.inv) continue;
        const pos = smoothRef.current[uid];
        if (pos) list.push({ uid, x: pos.x, y: pos.y, color: POKE[p.poke]?.color });
      }
      const d = dummyRef.current;
      if (d && !d.rsp && !d.inv) list.push({ uid: 'dummy', x: d.x, y: d.y, color: DUMMY.color });
      return list;
    };

    const landHit = (t, hit) => {
      if (t.uid === 'dummy') {
        const res = applyHit(dummyRef.current, DUMMY, hit);
        if (res === 'shield' || !res) return;
      } else {
        sendHit(t.uid, hit);
      }
      addSpark(t.x, t.y - HB_H / 2, '#fff', hit.dmg >= 10);
      addPop(t.x, t.y - HB_H - 8, `${hit.dmg}%`, '#fbbf24');
      const me = bodyRef.current;
      if (me && !me.freeze) me.freeze = Math.min(8, 2 + Math.round(hit.dmg / 3));
    };

    const tick = () => {
      frameRef.current++;
      const r = roundRef.current;
      const inp = inputRef.current;
      const now = serverNow();
      if (!r || r.phase === 'lobby' || !r.id) return;
      const fighterIds = Object.keys(r.fighters || {}).sort((a, b) =>
        ((netRef.current[a]?.joinedAt) || 0) - ((netRef.current[b]?.joinedAt) || 0) || a.localeCompare(b));
      const amFighter = fighterIds.includes(myUid) && !isSpectator;
      const live = r.phase === 'countdown' && now >= r.startAt;

      // A new round: set up my fighter at its spawn point (and a dummy if I'm alone)
      if (simRoundRef.current !== r.id) {
        simRoundRef.current = r.id;
        fxRef.current = { pops: [], sparks: [], blasts: [], shake: 0 };
        pendingHitsRef.current = [];
        bodyRef.current = null; dummyRef.current = null;
        if (amFighter) {
          const i = fighterIds.indexOf(myUid), n = fighterIds.length;
          const x = STAGE.x1 + 50 + (i + 0.5) * ((STAGE.x2 - STAGE.x1 - 100) / n);
          const b = newBody(x, x < W / 2 ? 1 : -1);
          b.jumps = (POKE[netRef.current[myUid]?.poke]?.jumps || 2) - 1;
          bodyRef.current = b;
          if (n === 1) {
            const d = newBody(W - x, x < W / 2 ? -1 : 1);
            d.st = Infinity;
            dummyRef.current = d;
          }
          sendState(r.id);
        }
      }

      // Ease everyone else towards their latest position (a little ahead, by their speed)
      const nowMs = performance.now();
      for (const [uid, p] of Object.entries(netRef.current)) {
        if (uid === myUid || p.rid !== r.id || p.x === undefined) continue;
        // Updates come less often inside Discord, so guess further ahead there
        const ahead = Math.min(activity ? 12 : 6, (nowMs - (p._recv || nowMs)) / STEP_MS);
        const tx = p.x + (p.vx || 0) * ahead, ty = p.y + (p.vy || 0) * ahead;
        const s = smoothRef.current[uid];
        if (!s || Math.abs(s.x - tx) > 160 || Math.abs(s.y - ty) > 160) smoothRef.current[uid] = { x: tx, y: ty };
        else { s.x += (tx - s.x) * 0.35; s.y += (ty - s.y) * 0.35; }
      }

      const me = bodyRef.current;
      const myPoke = POKE[netRef.current[myUid]?.poke] || ROSTER[0];
      if (me && live && amFighter) {
        // Hits that landed on me
        for (const hit of pendingHitsRef.current.splice(0)) {
          const res = applyHit(me, myPoke, hit);
          if (res === 'shield') addSpark(me.x, me.y - HB_H / 2, myPoke.color);
          else if (res) { addSpark(me.x, me.y - HB_H / 2, '#fff', hit.dmg >= 10); addPop(me.x, me.y - HB_H - 8, `${hit.dmg}%`, '#f87171'); if (res > 14) fxRef.current.shake = 8; }
        }
        const wasOut = me.out;
        const res = stepBody(me, myPoke, inp);
        if (res === 'ko' && !wasOut) { addBlast(me.x, me.y, myPoke.color); sendState(r.id); }

        // My attack hitting people
        const def = me.act && ATTACKS[me.act];
        if (def && def.box && me.actF >= def.s && me.actF < def.s + def.a && !me.freeze) {
          const box = attackBox(me, def);
          for (const t of targets(r)) {
            if (me.hitSet.has(t.uid) || !overlap(box, hurtbox(t.x, t.y))) continue;
            me.hitSet.add(t.uid);
            const dir = def.box[0] === 0 ? (Math.sign(t.x - me.x) || me.f) : me.f;
            landHit(t, { dmg: def.dmg, base: def.base, growth: def.growth, ang: def.ang, dir });
          }
        }
        // My projectiles hitting people
        for (const p of me.proj) {
          for (const t of targets(r)) {
            if (p.hit.has(t.uid)) continue;
            const hb = hurtbox(t.x, t.y);
            if (!overlap([p.x - p.r, p.y - p.r, p.r * 2, p.r * 2], hb)) continue;
            p.hit.add(t.uid); p.life = 0;
            landHit(t, { dmg: p.dmg, ...PROJ_HIT, dir: Math.sign(p.vx) || 1 });
          }
        }
        me.proj = me.proj.filter(p => p.life > 0);
        if (frameRef.current % SEND_EVERY === 0) sendState(r.id);
      }
      if (dummyRef.current && live) {
        const d = dummyRef.current;
        if (stepBody(d, DUMMY, EMPTY_INPUT()) === 'ko') addBlast(d.x, d.y, DUMMY.color);
      }
      // Presses only count for the frame they happened in
      inp.jumpP = inp.atkP = inp.spP = inp.downP = false;

      // Effects
      const fx = fxRef.current;
      for (const k of ['pops', 'sparks', 'blasts']) fx[k] = fx[k].filter(e => ++e.t < e.life);
      if (fx.shake > 0) fx.shake--;
    };

    const sendState = (rid) => {
      const b = bodyRef.current;
      if (!b || !mePath) return;
      const r1 = v => Math.round(v * 10) / 10;
      net.update(mePath, {
        rid, x: r1(b.x), y: r1(b.y), vx: r1(b.vx), vy: r1(b.vy), f: b.f,
        pct: Math.round(b.pct), st: b.st, out: b.out, rsp: b.rsp > 0, halo: b.halo > 0,
        act: b.act, actF: b.act ? b.actF : null, hs: b.hs > 0, tumble: b.tumble, spin: r1(b.spin),
        inv: b.inv > 0, sh: b.shielding ? Math.round(b.sh) : 0, flash: b.flash > 0,
        pj: b.proj.length ? b.proj.map(p => [r1(p.x), r1(p.y), r1(p.vx), p.r]) : null,
      }).catch(() => {});
    };

    const draw = () => {
      const r = roundRef.current;
      const frame = frameRef.current;
      const fx = fxRef.current;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (fx.shake) ctx.translate((Math.random() - 0.5) * fx.shake, (Math.random() - 0.5) * fx.shake);
      drawStage(ctx);
      if (!r || !r.id) return;
      const imgs = imgsRef.current;
      const fighterIds = Object.keys(r.fighters || {});
      const drawn = [];
      const cards = [];
      // Other fighters
      for (const uid of fighterIds) {
        const p = netRef.current[uid];
        if (uid === myUid) continue;
        if (!p) { cards.push({ name: 'Left', poke: null, pct: 0, st: 0, out: true }); continue; }
        cards.push({ name: p.name || 'Player', poke: p.poke, pct: p.rid === r.id ? p.pct || 0 : 0,
                     st: p.rid === r.id ? p.st ?? STOCKS : STOCKS, out: p.rid === r.id && p.out });
        if (p.rid !== r.id || p.out || p.rsp) continue;
        const s = smoothRef.current[uid];
        if (!s) continue;
        for (const pj of p.pj || []) drawProjectile(ctx, { x: pj[0], y: pj[1], r: pj[3] }, POKE[p.poke]?.color || '#fff', frame);
        drawn.push({ x: s.x, y: s.y, f: p.f || 1, poke: p.poke, act: p.act, actF: p.actF ?? undefined, spin: p.spin || 0,
                     inv: p.inv, sh: p.sh, flash: p.flash, halo: p.halo, name: p.name || 'Player', isMe: false });
      }
      // The dummy
      const d = dummyRef.current;
      if (d) {
        cards.push({ name: 'Dummy', poke: 'dummy', pct: d.pct, st: Infinity, out: false });
        if (!d.rsp) drawn.push({ x: d.x, y: d.y, f: d.f, poke: 'dummy', spin: d.spin, inv: d.inv > 0, flash: d.flash, halo: d.halo, name: 'Training dummy' });
      }
      // Me, on top
      const me = bodyRef.current;
      const myP = netRef.current[myUid];
      if (me && myP) {
        cards.unshift({ name: myP.name || 'You', poke: myP.poke, pct: me.pct, st: me.st, out: me.out, isMe: true });
        for (const pj of me.proj) drawProjectile(ctx, pj, POKE[myP.poke]?.color || '#fff', frame);
        if (!me.out && !me.rsp) {
          drawn.push({ x: me.x, y: me.y, f: me.f, poke: myP.poke, act: me.act, actF: me.actF, spin: me.spin, inv: me.inv > 0,
                       sh: me.shielding ? me.sh : 0, flash: me.flash, halo: me.halo > 0, name: myP.name || 'You', isMe: true });
        }
      }
      for (const s of drawn) drawFighter(ctx, imgs, s, frame);
      for (const s of drawn) drawOffscreen(ctx, imgs, s);

      // Sparks, damage numbers, KO blasts
      for (const e of fx.sparks) {
        const k = e.t / e.life, rad = (e.big ? 34 : 22) * (0.4 + k);
        ctx.globalAlpha = 1 - k; ctx.strokeStyle = e.color; ctx.lineWidth = 3;
        for (let i = 0; i < 8; i++) {
          const a = i * Math.PI / 4 + e.t * 0.05;
          ctx.beginPath(); ctx.moveTo(e.x + Math.cos(a) * rad * 0.4, e.y + Math.sin(a) * rad * 0.4);
          ctx.lineTo(e.x + Math.cos(a) * rad, e.y + Math.sin(a) * rad); ctx.stroke();
        }
      }
      for (const e of fx.pops) {
        ctx.globalAlpha = 1 - e.t / e.life; ctx.fillStyle = e.color; ctx.font = '900 14px system-ui, sans-serif';
        ctx.textAlign = 'center'; ctx.fillText(e.text, e.x, e.y - e.t * 0.6);
      }
      for (const e of fx.blasts) {
        const k = e.t / e.life;
        ctx.globalAlpha = 1 - k;
        const len = 120 + 400 * k, wid = 40 * (1 - k) + 6;
        ctx.save(); ctx.translate(e.x, e.y); ctx.rotate(e.ang);
        const g = ctx.createLinearGradient(0, 0, len, 0);
        g.addColorStop(0, '#fff'); g.addColorStop(0.3, e.color); g.addColorStop(1, 'transparent');
        ctx.fillStyle = g; ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(len, -wid); ctx.lineTo(len, wid); ctx.closePath(); ctx.fill();
        ctx.restore();
      }
      ctx.globalAlpha = 1;

      drawHud(ctx, imgs, cards);

      // Countdown, GO!, the winner
      const now = serverNow();
      if (r.phase === 'countdown' && now < r.startAt) bigText(ctx, String(Math.ceil((r.startAt - now) / 1000)), 96);
      else if (r.phase === 'countdown' && now - r.startAt < 700) bigText(ctx, 'GO!', 96, '#fbbf24');
      else if (r.phase === 'gameover') {
        ctx.fillStyle = 'rgba(0,0,0,0.45)'; ctx.fillRect(0, 0, W, H);
        bigText(ctx, r.winnerName ? `${r.winnerName} wins!` : 'Draw!', 46, '#fbbf24');
      }
      if (me && me.out && r.phase === 'countdown') {
        ctx.font = 'bold 14px system-ui, sans-serif'; ctx.fillStyle = '#f87171'; ctx.textAlign = 'center';
        ctx.fillText('You\'re out! Watching the rest of the battle…', W / 2, 26);
      }
      if (!fighterIds.includes(myUid)) {
        ctx.font = 'bold 13px system-ui, sans-serif'; ctx.fillStyle = '#a1a1aa'; ctx.textAlign = 'center';
        ctx.fillText('Spectating: you\'re in the next battle', W / 2, 24);
      }
    };

    let raf, last = performance.now(), acc = 0;
    const loop = t => {
      acc += Math.min(100, t - last); last = t;
      while (acc >= STEP_MS) { tick(); acc -= STEP_MS; }
      draw();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [arenaView, net, myUid, mePath, isSpectator]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Touch controls ────────────────────────────────────────────────────────
  const touch = (action) => ({
    onPointerDown: e => {
      e.preventDefault();
      e.currentTarget.setPointerCapture?.(e.pointerId);
      const inp = inputRef.current;
      if (action === 'jump') inp.jumpP = true;
      else if (action === 'attack') inp.atkP = true;
      else if (action === 'special') inp.spP = true;
      else { if (action === 'down' && !inp.down) inp.downP = true; inp[action] = true; }
    },
    onPointerUp: () => { if (action in inputRef.current) inputRef.current[action] = false; },
    onPointerCancel: () => { if (action in inputRef.current) inputRef.current[action] = false; },
    onContextMenu: e => e.preventDefault(),
  });

  const playerList = Object.values(players).sort((a, b) => (a.joinedAt || Infinity) - (b.joinedAt || Infinity));
  const myPlayer = players[myUid];
  const myPoke = myPlayer ? POKE[myPlayer.poke] : null;

  // ─── RENDER: Not logged in ─────────────────────────────────────────────────
  if (!discordUser) {
    return (
      <div className="relative flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center">
        <div className="fixed left-0 right-0 bottom-0 pointer-events-none"
          style={{ top: '100px', backgroundImage: 'url(/arena.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />
        <div className="fixed left-0 right-0 bottom-0 pointer-events-none" style={{ top: '100px', background: 'rgba(0,0,0,0.92)' }} />
        <div className="relative z-10 text-5xl">⚔️</div>
        <div className="relative z-10">
          <h2 className="text-white font-bold text-xl" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8)' }}>Battle Arena</h2>
          <p className="text-white text-sm mt-1" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>Go head to head with other viewers live on stream!</p>
        </div>
        <div className="relative z-10 flex flex-col gap-1 rounded-xl px-4 py-3 border border-zinc-800/40 backdrop-blur-sm" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <p className="text-white text-sm">You need to log in with Discord to join the battle!</p>
          <button onClick={onLogin}
            className="flex items-center justify-center gap-1.5 text-white font-semibold text-xs rounded-lg px-4 py-2 transition-all duration-200 active:scale-95"
            style={{ background: 'linear-gradient(135deg, #04aff7, #f42ae4)' }}>
            <LogIn className="h-3.5 w-3.5 flex-shrink-0" />
            Log in with Discord
          </button>
        </div>
      </div>
    );
  }

  // ─── RENDER: Character select ──────────────────────────────────────────────
  if (!joined) {
    return (
      <div className="px-3 py-4 max-w-5xl mx-auto w-full">
        <style>{`
          @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
          .poke-card:hover img { animation: float 0.8s ease-in-out infinite; }
          .poke-card { transition: all 0.15s cubic-bezier(0.34,1.56,0.64,1); }
          .poke-card:hover { transform: scale(1.06); }
          .poke-card:active { transform: scale(0.95); }
        `}</style>
        <div className="text-center mb-5">
          <div className="text-3xl mb-2">⚔️</div>
          <h2 className="text-white font-bold text-lg">Choose your fighter</h2>
          <p className="text-zinc-400 text-xs mt-1">
            {phase === 'lobby' ? 'Pick a Pokémon to battle with' : 'A battle is on! Pick a Pokémon to watch it and join the next one'}
          </p>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2.5 sm:gap-3">
          {ROSTER.map(poke => (
            <button key={poke.id}
              className="poke-card bg-gradient-to-br from-zinc-800/85 to-black/90 border border-zinc-700/70 rounded-2xl p-3 flex flex-col items-center gap-1.5 active:scale-95 w-full"
              onClick={() => joinLobby(poke.id)}>
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center" style={{ background: `${poke.color}22` }}>
                <img src={sprite(poke.dex)} alt={poke.name} className="w-12 h-12 sm:w-14 sm:h-14 object-contain" style={{ imageRendering: 'pixelated' }} />
              </div>
              <span className="text-white font-bold text-[10px] sm:text-xs">{poke.name}</span>
              <span className="text-zinc-400 text-[8px] sm:text-[9px] text-center leading-tight">{poke.special}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ─── RENDER: Lobby ─────────────────────────────────────────────────────────
  if (!arenaView) {
    const readyCount = playerList.filter(p => p.ready).length;
    return (
      <div className="px-3 py-4 max-w-2xl mx-auto w-full">
        <div className="bg-gradient-to-br from-zinc-800/85 to-black/90 border border-zinc-700/70 rounded-2xl p-4 mb-3 text-center">
          <div className="text-3xl mb-2">⚔️</div>
          <h2 className="text-white font-bold text-lg">Battle Lobby</h2>
          <p className="text-zinc-400 text-xs mt-1">{playerList.length} in the lobby · up to {MAX_FIGHTERS} fight at once · {STOCKS} stocks each</p>
          <p className="text-zinc-500 text-[11px] mt-2 leading-snug">
            Knock everyone off the stage. Hits build up damage %, and the higher it is, the further they fly.
          </p>
        </div>

        {myPoke && myPlayer && (
          <div className="bg-gradient-to-br from-zinc-800/85 to-black/90 border border-cyan-500/30 rounded-2xl p-3 mb-3 flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${myPoke.color}33` }}>
              <img src={sprite(myPoke.dex)} alt={myPoke.name} className="w-10 h-10 object-contain" style={{ imageRendering: 'pixelated' }} />
            </div>
            <div>
              <p className="text-cyan-400 font-bold text-xs">Your fighter</p>
              <p className="text-white font-bold text-sm">{myPoke.name}</p>
              <p className="text-zinc-400 text-[10px]">{myPoke.special}</p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <button onClick={leaveArena} className="text-zinc-400 text-xs px-2 py-1 rounded-lg border border-zinc-700/70 active:scale-95">Leave</button>
              <button onClick={toggleReady}
                className="text-xs font-bold px-3 py-1.5 rounded-lg active:scale-95 transition-all"
                style={myPlayer.ready
                  ? { background: 'rgba(74,222,128,0.15)', color: '#4ade80', border: '1px solid rgba(74,222,128,0.4)' }
                  : { background: 'linear-gradient(135deg,#04aff7,#f42ae4)', color: 'white' }}>
                {myPlayer.ready ? '✓ Ready' : 'Ready Up'}
              </button>
            </div>
          </div>
        )}

        <div className="bg-zinc-900/40 border border-zinc-800/40 rounded-2xl p-3 mb-3">
          <p className="text-zinc-400 text-xs font-semibold mb-2 uppercase tracking-wider">Players in lobby</p>
          <div className="flex flex-wrap gap-2">
            {playerList.map(p => {
              const poke = POKE[p.poke];
              return (
                <div key={p.uid} className="flex items-center gap-1.5 bg-zinc-800/40 rounded-xl px-2 py-1.5">
                  {poke && <img src={sprite(poke.dex)} alt={poke.name} className="w-6 h-6 object-contain" style={{ imageRendering: 'pixelated' }} />}
                  <span className="text-white text-[10px] font-semibold">{p.name}</span>
                  {p.uid === myUid && <span className="text-cyan-400 text-[8px]">(you)</span>}
                  {p.ready && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(74,222,128,0.15)', color: '#4ade80' }}>READY</span>}
                </div>
              );
            })}
          </div>
        </div>

        <p className="text-center text-zinc-400 text-xs">
          {readyCount < playerList.length
            ? `Waiting for everyone to ready up (${readyCount}/${playerList.length} ready)…`
            : 'Everyone\'s ready: starting!'}
        </p>
        {playerList.length === 1 && (
          <p className="text-center text-zinc-500 text-[11px] mt-1">On your own? Ready up to practise against a training dummy.</p>
        )}
      </div>
    );
  }

  // ─── RENDER: Battle ────────────────────────────────────────────────────────
  const fighting = !!round?.fighters?.[myUid] && phase === 'countdown';
  const padBtn = 'flex items-center justify-center select-none rounded-2xl text-white font-black active:scale-90 transition-transform';
  const padStyle = { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)', touchAction: 'none' };
  return (
    <div className="flex flex-col w-full max-w-4xl mx-auto" style={{ userSelect: 'none', WebkitUserSelect: 'none' }}>
      {/* Inside Discord the window is short, so the stage shrinks to leave room for the buttons under it */}
      <canvas ref={canvasRef} className="w-full block bg-black mx-auto" style={{ aspectRatio: `${W} / ${H}`, touchAction: 'none',
        ...(activity ? { maxWidth: `calc((100vh - ${fighting ? 250 : 60}px) * ${W / H})` } : {}) }} />

      {fighting && (
        <div className="px-3 pt-3 pb-3 bg-gradient-to-br from-zinc-800/85 to-black/90" style={{ touchAction: 'none' }}>
          <div className="flex items-center justify-between gap-3">
            {/* Direction pad */}
            <div className="grid grid-cols-3 grid-rows-3 gap-1" style={{ width: 150, height: 150 }}>
              <span />
              <button className={padBtn} style={padStyle} {...touch('up')}>▲</button>
              <span />
              <button className={padBtn} style={padStyle} {...touch('left')}>◀</button>
              <span />
              <button className={padBtn} style={padStyle} {...touch('right')}>▶</button>
              <span />
              <button className={padBtn} style={padStyle} {...touch('down')}>▼</button>
              <span />
            </div>
            {/* Buttons */}
            <div className="grid grid-cols-2 gap-2" style={{ width: 150 }}>
              <button className={`${padBtn} h-16 text-xs`} style={{ background: 'linear-gradient(135deg,#ef4444,#dc2626)', touchAction: 'none' }} {...touch('attack')}>ATTACK</button>
              <button className={`${padBtn} h-16 text-xs`} style={{ background: 'linear-gradient(135deg,#f59e0b,#f42ae4)', touchAction: 'none' }} {...touch('special')}>SPECIAL</button>
              <button className={`${padBtn} h-16 text-xs`} style={{ background: 'linear-gradient(135deg,#04aff7,#6366f1)', touchAction: 'none' }} {...touch('jump')}>JUMP</button>
              <button className={`${padBtn} h-16 text-xs`} style={{ ...padStyle }} {...touch('shield')}>SHIELD</button>
            </div>
          </div>
          <p className="text-center text-zinc-500 text-[10px] mt-3 leading-relaxed">
            Keyboard: A/D or ←/→ move · W/S or ↑/↓ aim · Space jump (again in the air) · J attack · K special · L shield<br />
            Hold a direction when you attack for a different move · ↑ + special to recover · ↓ drops through platforms
          </p>
        </div>
      )}

      <div className="flex justify-center py-2">
        <button onClick={leaveArena} className="text-zinc-400 text-xs px-3 py-1 rounded-lg border border-zinc-700/70 active:scale-95">Leave</button>
      </div>
    </div>
  );
}
